import React, {useEffect, useState} from "react";
import {Box, Button, Grid, IconButton, Typography, useTheme} from "@mui/material";
import {Delete, EditRounded} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import ContentBody from "../../common/content-body/ContentBody";
import { FormAutocomplete } from "../../common/form-fields-wrappers";
import {IRoyaltiesContractor} from "../model/model";
import { updateContractorRoyalties } from "../service/contractorSearch.actions";
import {NetworkScope} from "../../types";
import {useHasScope} from "../../../../config/globalConfig";


const RoyaltiesVoices = (props: any) => {
    const hasScope = useHasScope;
    const {t} = useTranslation(['network', 'translation']);
    const location:any = useLocation();
    const rateableList = [
        "Invoiced amount",
        "60% of Time + Km",
        "60% of rateable"
    ];
    const rulesList = [
        "VAT (iva)",
        "Fines (multe e contravvenzioni)",
        "Damages (danni)",
        "Fuel (carburante)",
        "Refueling service (servizio di refueling)",
        "Insurance cover (coperture assicurative)",
        "Airport charges (oneri aeroportuali)",
        "OneWay (Oneway)",
        "Time + Km (tempo + km)"
    ];

    const navigate = useNavigate();

    const [voicesList, setVoicesList] = useState(rulesList);
    const [splitOffList, setSplitOffList] = useState(rulesList);

    const handleDisableForm = (value:boolean) => {
        setIsDisabled(value);
    };
    const theme = useTheme();

    const royaltiesForm = useForm<IRoyaltiesContractor>({
        defaultValues: {
            rateable: location.state.contractor?.rateable ?? "",
            voices: location.state.contractor?.voices ?? [],
            splitOffs: location.state.contractor?.splitOffs ?? []
        }
    });

    const voicesRulesArray = useFieldArray({name: 'voices', control: royaltiesForm.control});
    const splitOffsRulesArray = useFieldArray({name: 'splitOffs', control: royaltiesForm.control});

    useEffect(() => {
        setVoicesList(rulesList.filter((item: any) => !voicesRulesArray.fields.some(x => x.type === item)));
    }, [voicesRulesArray.fields]);

    useEffect(() => {
        setSplitOffList(rulesList.filter((item: any) => !splitOffsRulesArray.fields.some(x => x.type === item)));
    }, [splitOffsRulesArray.fields])

    const onSubmit = (data: any) => {
        const reqParams = {
            id: location.state.contractor?.id,
            rateable: data.rateable,
            voices: data.voices,
            splitOffs: data.splitOffs
        }
        updateContractorRoyalties(reqParams)
            .then((res: any) => {
                setVoicesList(res.voices);
                setSplitOffList(res.splitOffs);
            })
        setIsDisabled(true);
    }
    const [isDisabled, setIsDisabled] = useState(true);

    const handleBackStep = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        let path = `/mobility-store/contractor-royalties`;
        navigate(path);
    }


    return(
        <>
            <SectionHeader title={t("contractorMng.royaltiesVoices.title")}/>
            <ContentBody>
                <form onSubmit={royaltiesForm.handleSubmit(onSubmit)}>
                    <Box sx={{display: 'flex',  justifyContent: 'flex-end', mt: theme.spacing(2), mb:theme.spacing(2)}}>
                        {hasScope(NetworkScope.MsMng) && isDisabled ?
                            <IconButton color={"accent"} onClick={() => {handleDisableForm(false)}}>
                                <EditRounded/>
                            </IconButton> :
                            <Button
                                autoFocus
                                onClick={royaltiesForm.handleSubmit(onSubmit)}
                            >
                                {t("contractorMng.royaltiesVoices.button.confirm")}
                            </Button>
                        }
                    </Box>

                    <Typography variant={"h6"}>
                        {t("contractorMng.royaltiesVoices.rateable")}
                    </Typography>

                        <FormAutocomplete<IRoyaltiesContractor>
                            disabled={isDisabled}
                            control={royaltiesForm.control}
                            name="rateable"
                            options={rateableList}
                            label={t("contractorMng.royaltiesVoices.rateable")}
                            getOptionLabel={(option: any) => option ? t(`contractorMng.royaltiesVoices.retableValues.${option}`) : ''}
                        />


                    <Typography variant={"h6"} sx={{mt: theme.spacing(2)}}>
                        {t("contractorMng.royaltiesVoices.splitOff")}
                    </Typography>

                        {(isDisabled === false && splitOffsRulesArray.fields.length < rulesList.length)  &&
                            <Grid item container xs={12} md={12} justifyContent="flex-end" alignItems="flex-end">
                                <Box sx={{my: theme.spacing(2)}}>
                                    <Button variant="text" color="accent" onClick={() => splitOffsRulesArray.append({ type: "" })}>
                                        {t("contractorMng.royaltiesVoices.button.addSplitOff")}
                                    </Button>
                                </Box>
                            </Grid>
                       }

                        {splitOffsRulesArray.fields.length === 0 &&
                            <Grid item container xs={12} md={12} align-items="center" justifyContent="center">
                                <p>{t("contractorMng.royaltiesVoices.message.noSplitOffRegistered")}</p>
                            </Grid>
                        }

                        <Grid item container xs={12} md={12} >
                            {splitOffsRulesArray.fields.map((field, index) => {
                                return (
                                    <Grid key={field.id} item container xs={12} md={12} columnSpacing={2}>

                                        <Grid item xs={12} md={isDisabled === false ? 11 : 12}>
                                            <FormAutocomplete<IRoyaltiesContractor>
                                                name={`splitOffs.${index}.type`}
                                                control={royaltiesForm.control}
                                                options={splitOffList}
                                                label={t("contractorMng.royaltiesVoices.form.label.splitOff")}
                                                disabled={isDisabled}
                                                filterSelectedOptions
                                                onChange={(_e, value) => splitOffsRulesArray.update(index, {type: value})}
                                            />
                                        </Grid>

                                        {isDisabled === false &&
                                            <Grid item>
                                                <IconButton
                                                    sx={{mt: theme.spacing(2), ml: 'auto', mr: theme.spacing(2)}}
                                                    onClick={() => {splitOffsRulesArray.remove(index)}}
                                                >
                                                    <Delete/>
                                                </IconButton>
                                            </Grid>
                                        }
                                    </Grid>
                                );
                            })}
                        </Grid>


                    <Typography variant="h6" sx={{mt: theme.spacing(2)}}>{t("Voices")}</Typography>

                        {(isDisabled === false && voicesRulesArray.fields.length < rulesList.length) &&
                            <Grid item container xs={12} md={12} justifyContent="flex-end" alignItems="flex-end">
                                <Box sx={{my: theme.spacing(2)}}>
                                    <Button variant={'text'} color={"accent"} onClick={() => voicesRulesArray.append({type: ""})}>
                                        {t("contractorMng.royaltiesVoices.button.addVoice")}
                                    </Button>
                                </Box>
                            </Grid>
                        }

                        {voicesRulesArray.fields.length === 0 &&
                            <Grid item container xs={12} md={12} align-items="center" justifyContent="center">
                                <p>{t("contractorMng.royaltiesVoices.message.noVoiceRegistered")}</p>
                            </Grid>
                        }

                        <Grid item container xs={12} md={12} >
                            {voicesRulesArray.fields.map((field, index) => {
                                return (
                                    <Grid key={field.id} item container xs={12} md={12} columnSpacing={2}>
                                        <Grid item xs={12} md={isDisabled === false ? 11 : 12}>
                                            <FormAutocomplete<IRoyaltiesContractor>
                                                name={`voices.${index}.type`}
                                                control={royaltiesForm.control}
                                                options={voicesList}
                                                label={t("contractorMng.royaltiesVoices.form.label.voices")}
                                                disabled={isDisabled}
                                                filterSelectedOptions
                                                onChange={(_e, value) => voicesRulesArray.update(index, {type: value})}
                                            />
                                        </Grid>

                                        {isDisabled === false &&
                                            <Grid item>
                                                <IconButton
                                                    sx={{mt: theme.spacing(2), ml: 'auto', mr: theme.spacing(2)}}
                                                    onClick={() => { voicesRulesArray.remove(index) }}
                                                >
                                                    <Delete/>
                                                </IconButton>
                                            </Grid>
                                        }
                                    </Grid>
                                );
                            })}
                        </Grid>
                </form>
            </ContentBody>
            <Button variant={'text'} color={"accent"}  onClick={handleBackStep}>
                {t("contractorMng.royaltiesVoices.button.back")}
            </Button>
        </>
    )
}

export default RoyaltiesVoices;